import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import './TrainingEventCalendar.css';

const WEEK_DAYS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

function toDate(value) {
	if (!value) return null;
	const date = new Date(String(value).replace(' ', 'T'));
	return Number.isNaN(date.getTime()) ? null : date;
}

function isSameDay(a, b) {
	return (
		a &&
		b &&
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate()
	);
}

function isSameMonth(a, b) {
	return (
		a &&
		b &&
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth()
	);
}

function startOfMonth(date) {
	return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, amount) {
	return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function formatMonthYear(date) {
	return new Intl.DateTimeFormat('fr-FR', {
		month: 'long',
		year: 'numeric',
	}).format(date);
}

function formatLongDate(date) {
	return new Intl.DateTimeFormat('fr-FR', {
		weekday: 'long',
		day: '2-digit',
		month: 'long',
		year: 'numeric',
	}).format(date);
}

function buildCalendarDays(visibleMonth) {
	const firstDayOfMonth = startOfMonth(visibleMonth);
	const startWeekDay = firstDayOfMonth.getDay();
	const gridStart = new Date(firstDayOfMonth);
	gridStart.setDate(firstDayOfMonth.getDate() - startWeekDay);

	return Array.from({ length: 42 }, (_, index) => {
		const day = new Date(gridStart);
		day.setDate(gridStart.getDate() + index);
		return day;
	});
}

export default function TrainingEventCalendar({
	currentTraining,
	allTrainings = [],
	compact = false,
	nextCount = 3,
}) {
	const currentDate = useMemo(
		() => toDate(currentTraining?.startdate),
		[currentTraining?.startdate],
	);

	const [visibleMonth, setVisibleMonth] = useState(
		currentDate ? startOfMonth(currentDate) : startOfMonth(new Date()),
	);
	const [turnDirection, setTurnDirection] = useState('next');
	const [turnKey, setTurnKey] = useState(0);

	useEffect(() => {
		if (currentDate) {
			setVisibleMonth(startOfMonth(currentDate));
		}
	}, [currentDate]);

	const relatedEventDates = useMemo(() => {
		if (!currentTraining?.title) {
			return currentDate ? [currentDate].filter(Boolean) : [];
		}

		const sameTrainings = allTrainings.filter(
			(item) =>
				item?.title &&
				currentTraining?.title &&
				item.title.trim().toLowerCase() ===
					currentTraining.title.trim().toLowerCase(),
		);

		const allDates = sameTrainings
			.map((item) => toDate(item.startdate))
			.filter(Boolean);

		if (currentDate && !allDates.some((date) => isSameDay(date, currentDate))) {
			allDates.push(currentDate);
		}

		return allDates.sort((a, b) => a.getTime() - b.getTime());
	}, [allTrainings, currentTraining?.title, currentDate]);

	const upcomingEventDates = useMemo(() => {
		const now = new Date();
		now.setHours(0, 0, 0, 0);

		return relatedEventDates.filter((date) => date >= now);
	}, [relatedEventDates]);

	const nextEventDates = useMemo(() => {
		return upcomingEventDates.slice(0, nextCount);
	}, [upcomingEventDates, nextCount]);

	const days = useMemo(() => buildCalendarDays(visibleMonth), [visibleMonth]);

	const goToPreviousMonth = () => {
		setTurnDirection('prev');
		setTurnKey((prev) => prev + 1);
		setVisibleMonth((prev) => addMonths(prev, -1));
	};

	const goToNextMonth = () => {
		setTurnDirection('next');
		setTurnKey((prev) => prev + 1);
		setVisibleMonth((prev) => addMonths(prev, 1));
	};

	return (
		<div
			className={`training-calendar ${compact ? 'training-calendar--compact' : ''}`}>
			<div className='training-calendar__shell'>
				<div className='training-calendar__header'>
					<div className='training-calendar__title-wrap'>
						<CalendarDays className='training-calendar__title-icon' size={18} />

						<h3 className='training-calendar__title'>Calendrier</h3>
					</div>

					<div className='training-calendar__nav'>
						<button
							type='button'
							className='training-calendar__nav-btn'
							onClick={goToPreviousMonth}
							aria-label='Mois précédent'>
							<ChevronLeft size={18} />
						</button>
						<button
							type='button'
							className='training-calendar__nav-btn'
							onClick={goToNextMonth}
							aria-label='Mois suivant'>
							<ChevronRight size={18} />
						</button>
					</div>
				</div>

				<div className='training-calendar__board'>
					<div className='training-calendar__month-row'>
						<h4 className='training-calendar__month'>
							{formatMonthYear(visibleMonth)}
						</h4>
					</div>

					<div
						key={`${visibleMonth.getFullYear()}-${visibleMonth.getMonth()}-${turnKey}`}
						className={`training-calendar__page training-calendar__page--${turnDirection}`}>
						<div className='training-calendar__weekdays'>
							{WEEK_DAYS.map((day, index) => (
								<span key={`${day}-${index}`}>{day}</span>
							))}
						</div>

						<div className='training-calendar__grid'>
							{days.map((day) => {
								const isCurrentMonth = isSameMonth(day, visibleMonth);
								const isMainEventDate = isSameDay(day, currentDate);
								const isUpcomingEventDate = upcomingEventDates.some(
									(eventDate) => isSameDay(eventDate, day),
								);

								return (
									<div
										key={day.toISOString()}
										className={[
											'training-calendar__day',
											!isCurrentMonth && 'training-calendar__day--muted',
											isUpcomingEventDate && 'training-calendar__day--event',
											isMainEventDate && 'training-calendar__day--active',
										]
											.filter(Boolean)
											.join(' ')}>
										<span>{day.getDate()}</span>
									</div>
								);
							})}
						</div>
					</div>
				</div>

				{nextEventDates.length ? (
					<div className='training-calendar__details'>
						<div className='training-calendar__next'>
							<p className='training-calendar__next-title'>
								Prochaines sessions
							</p>

							<div className='training-calendar__next-list'>
								{nextEventDates.map((date) => (
									<div
										key={date.toISOString()}
										className='training-calendar__next-item'>
										<span className='training-calendar__next-dot' />
										<span>{formatLongDate(date)}</span>
									</div>
								))}
							</div>
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
}
